import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import {
  AddCorrespondence,
  ListCorrespondence,
} from 'src/constants/ApiConstants';
import { GetApiBaseUrl } from 'src/constants/UrlConstants';
import { emitApiErrorMetrics, MetricsName } from 'src/metrics';
import {
  AddCorrespondenceTypeRequest,
  AddCorrespondenceTypeResponse,
  CorrespondenceDetails,
  ListCorrespondenceTypeRequest,
  ListCorrespondenceTypeResponse,
} from './models/CorrespondenceTypes';
import * as KatalMetrics from '@amzn/katal-metrics';
import { logger } from 'src/logger';

const CORRESPONDENCE_TAG = 'Correspondence';

export const correspondenceApi = createApi({
  reducerPath: 'correspondenceApi',
  baseQuery: retry(
    fetchBaseQuery({
      baseUrl: GetApiBaseUrl(),
    }),
  ),
  tagTypes: [CORRESPONDENCE_TAG],
  endpoints: (builder) => ({
    /**
     * API definition for listing the correspondence data
     */
    listCorrespondence: builder.query<
      ListCorrespondenceTypeResponse,
      Partial<ListCorrespondenceTypeRequest>
    >({
      query: (body) => ({
        url: ListCorrespondence.getRoute(),
        method: ListCorrespondence.method,
        body: JSON.stringify(body),
        responseHandler: 'content-type',
      }),
      providesTags: [CORRESPONDENCE_TAG],
      extraOptions: {
        maxRetries: ListCorrespondence.numberOfRetries,
      },
      async onQueryStarted(request, { queryFulfilled }) {
        const metric = new KatalMetrics.Metric.HttpRequest(
          MetricsName.BACKEND_REQUEST,
        ).withMonitor();
        try {
          logger.info(
            `Calling listCorrespondence API with request: ${JSON.stringify(
              request,
            )}`,
          );
          const { data } = await queryFulfilled;
          const dataToBeLogged = data?.correspondenceDetailsList.map(
            (corrrespondence: CorrespondenceDetails) =>
              corrrespondence.correspondenceId,
          );
          logger.info(
            `Response for listCorrespondence API is: ${JSON.stringify(
              dataToBeLogged,
            )}`,
          );
          metric.setSuccess();
        } catch (error: any) {
          logger.error(
            `Error occured while fetching listCorrespondence API ${JSON.stringify(
              error,
            )}`,
          );
          emitApiErrorMetrics(error, ListCorrespondence.metricsPublisher);
          metric.setFailure();
        }
        ListCorrespondence.metricsPublisher.publish(metric);
      },
    }),

    /**
     * API definition for Adding the correspondence data
     */
    addCorrespondence: builder.mutation<
      AddCorrespondenceTypeResponse,
      Partial<AddCorrespondenceTypeRequest>
    >({
      query: (body) => ({
        url: AddCorrespondence.getRoute(),
        method: AddCorrespondence.method,
        body: JSON.stringify(body),
        responseHandler: 'content-type',
      }),
      invalidatesTags: [CORRESPONDENCE_TAG],
      extraOptions: {
        maxRetries: AddCorrespondence.numberOfRetries,
      },
      async onQueryStarted(request, { queryFulfilled }) {
        const metric = new KatalMetrics.Metric.HttpRequest(
          MetricsName.BACKEND_REQUEST,
        ).withMonitor();
        try {
          logger.info(
            `Calling addCorrespondence API with request: ${JSON.stringify(
              request,
            )}`,
          );
          const { data } = await queryFulfilled;
          logger.info(
            `Response for addCorrespondence API is: ${JSON.stringify(data)}`,
          );
          metric.setSuccess();
        } catch (error: any) {
          logger.error(
            `Error occured while fetching addCorrespondence API ${JSON.stringify(
              error,
            )}`,
          );
          emitApiErrorMetrics(error, AddCorrespondence.metricsPublisher);
          metric.setFailure();
        }
        AddCorrespondence.metricsPublisher.publish(metric);
      },
    }),
  }),
});

export const { useListCorrespondenceQuery, useAddCorrespondenceMutation } =
  correspondenceApi;
